/*
  assets/b-read-more.js  (#433)  Custom element <b-read-more> for blocks/b-read-more.liquid.

  Collapses its content to data-height px with a fade, but only when the content is
  taller than that (plus a quarter, so a few extra lines never hide behind a button).
  The content stays in the DOM either way. Opens itself when focus moves into the
  hidden part or the theme editor selects a block inside it.
  Emits `b-read-more:toggle` (bubbles) with detail.open.

  data-split (#573): when the content holds a real <!-- split --> comment (found in the DOM, so
  the same text inside an attribute or a script never counts), everything above it shows in
  full, with no fade, and everything below it sits behind Read more. Without one it falls back
  to data-height.

  Links into it (#610): when the address's #id names an element inside the content, on first sight
  (a link from another page), on a later hash change, or on a plain click on a link to this same page
  whose hash names it, the Read more opens and the element scrolls into view. The id is looked up with
  getElementById, never as a selector. After a first-sight landing, one correction runs on load (images
  above move the target), unless the shopper has scrolled, tapped, pressed a key or used the toggle.
  Without JavaScript nothing collapses, so the browser's own anchor scrolling already lands there.
*/
if (!customElements.get('b-read-more')) {
  class BReadMore extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector(':scope > .b-read-more__content');
      this.button = this.querySelector(':scope > .b-read-more__toggle');
      if (!this.content || !this.button) return;

      this.height = parseInt(this.dataset.height, 10) || 200;
      this.split = this.hasAttribute('data-split') ? this.findSplit() : null;
      if (this.split) this.setAttribute('data-split-found', '');
      this.style.setProperty('--b-read-more-height', `${this.height}px`);
      if (this.dataset.fade) this.style.setProperty('--b-read-more-fade', this.dataset.fade);

      this.onClick = () => {
        this.cancelLanding();
        this.setOpen(this.hasAttribute('data-collapsed'));
      };
      this.onFocusIn = (event) => {
        if (this.hasAttribute('data-collapsed') && this.content.contains(event.target)) this.setOpen(true);
      };
      this.onBlockSelect = (event) => {
        if (this.contains(event.target)) this.setOpen(true);
      };
      this.onLoad = () => {
        this.measure();
        this.finishLanding();
        this.resetClip();
      };
      this.onHashChange = () => this.openFor(this.hashTarget());
      this.onLinkClick = (event) => this.linkClick(event);
      this.onTakeOver = () => this.cancelLanding();
      this.onResize = () => this.split && this.splitHeight();

      this.button.addEventListener('click', this.onClick);
      this.content.addEventListener('focusin', this.onFocusIn);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      window.addEventListener('load', this.onLoad, { once: true });
      window.addEventListener('resize', this.onResize);
      window.addEventListener('hashchange', this.onHashChange);
      document.addEventListener('click', this.onLinkClick);
      if (this.split) {
        // Lazy images and late web fonts above the marker move it after load: re-measure then too.
        this.content.addEventListener('load', this.onResize, true);
        document.fonts?.ready.then(this.onResize);
        document.fonts?.addEventListener('loadingdone', this.onResize);
      }

      this.measure();
    }

    disconnectedCallback() {
      if (!this.button) return;
      this.button.removeEventListener('click', this.onClick);
      this.content.removeEventListener('focusin', this.onFocusIn);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      window.removeEventListener('load', this.onLoad);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('hashchange', this.onHashChange);
      document.removeEventListener('click', this.onLinkClick);
      this.cancelLanding();
      this.content.removeEventListener('load', this.onResize, true);
      document.fonts?.removeEventListener('loadingdone', this.onResize);
    }

    // The first comment node reading "split", with an empty block placed after it to measure from.
    findSplit() {
      const walker = document.createTreeWalker(this.content, NodeFilter.SHOW_COMMENT);
      for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (node.data.trim().toLowerCase() !== 'split') continue;
        const mark = document.createElement('span');
        mark.className = 'b-read-more__split';
        mark.setAttribute('aria-hidden', 'true');
        node.after(mark);
        return mark;
      }
      return null;
    }

    // Collapsed height = where the split mark sits, re-read when images load or the width changes.
    splitHeight() {
      const top = this.split.getBoundingClientRect().top - this.content.getBoundingClientRect().top;
      this.height = Math.max(0, Math.round(top));
      this.style.setProperty('--b-read-more-height', `${this.height}px`);
    }

    // Collapse only on first sight, and only when there is enough to hide.
    measure() {
      if (this.split) {
        this.splitHeight();
        if (this.measured || this.content.scrollHeight - this.height < 8) return;
      } else {
        if (this.measured) return;
        if (this.content.scrollHeight <= this.height * 1.25) return;
      }
      this.measured = true;
      this.button.hidden = false;
      const target = this.hashTarget();
      if (target) {
        // A link to something inside: stay open and land on it, then correct once on load.
        this.setOpen(true, true);
        this.reveal(target);
        this.startLanding(target);
        return;
      }
      this.setOpen(false, true);
    }

    // The element the address's #id names, when it sits inside this Read more; otherwise null.
    hashTarget(hash = location.hash) {
      if (!hash || hash.length < 2) return null;
      let id = hash.slice(1);
      try { id = decodeURIComponent(id); } catch { /* a badly encoded hash is looked up as written */ }
      const el = document.getElementById(id);
      return el && this.content.contains(el) ? el : null;
    }

    // Open (when collapsed) and scroll to a target inside; the toggle event fires as for a click.
    openFor(target) {
      if (!target) return;
      this.cancelLanding();
      if (this.hasAttribute('data-collapsed')) this.setOpen(true);
      this.reveal(target);
    }

    reveal(target) {
      target.scrollIntoView({ block: 'start' });
    }

    // A plain click on a link to this same page (origin, path and query) whose hash names a target
    // inside. Clicking the hash the address already shows fires no hashchange, so it's handled here.
    linkClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
      if (!link || link.hasAttribute('download')) return;
      const target = link.getAttribute('target');
      if (target && target !== '_self') return;
      let url;
      try { url = new URL(link.href, location.href); } catch { return; }
      if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search) return;
      const el = this.hashTarget(url.hash);
      if (!el) return;
      // Let the browser update the address first, then open and land, unless a listener after this one
      // cancelled the click, so no navigation happened.
      requestAnimationFrame(() => {
        if (!event.defaultPrevented) this.openFor(el);
      });
    }

    // One correction on load after a first-sight landing, dropped as soon as the shopper takes over.
    startLanding(target) {
      this.landing = target;
      for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
        window.addEventListener(type, this.onTakeOver, { passive: true, capture: true });
      }
      if (document.readyState === 'complete') requestAnimationFrame(() => this.finishLanding());
    }

    cancelLanding() {
      if (!this.landing) return;
      this.landing = null;
      for (const type of ['wheel', 'touchstart', 'pointerdown', 'keydown']) {
        window.removeEventListener(type, this.onTakeOver, { capture: true });
      }
    }

    finishLanding() {
      const target = this.landing;
      if (!target) return;
      this.cancelLanding();
      if (!this.hasAttribute('data-collapsed') && this.hashTarget() === target) this.reveal(target);
    }

    // The browser's own #id scrolling (retried while the page loads) can scroll the clipped content
    // itself, so the collapsed view would start mid-way; a collapsed Read more always shows its top.
    resetClip() {
      if (this.hasAttribute('data-collapsed')) this.content.scrollTop = 0;
    }

    setOpen(open, silent = false) {
      this.toggleAttribute('data-collapsed', !open);
      this.resetClip();
      this.button.setAttribute('aria-expanded', String(open));
      const label = this.button.querySelector('.b-read-more__label');
      if (label) label.textContent = open ? this.button.dataset.labelLess : this.button.dataset.labelMore;
      if (silent) return;
      if (!open && this.getBoundingClientRect().top < 0) this.scrollIntoView({ block: 'start' });
      this.dispatchEvent(new CustomEvent('b-read-more:toggle', { bubbles: true, detail: { open } }));
    }
  }

  customElements.define('b-read-more', BReadMore);
}
